import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, PlusCircle, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTechAuth } from '@/contexts/TechAuthContext';
import {
  createTechDiagnosticSession,
  getTechCurrentAssignment,
  listTechDiagnosticSessions,
  TechDiagnosticSession,
  TechDiagnosticSessionStatus,
} from '@/lib/tech-api-client';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatSessionStatus(raw: TechDiagnosticSessionStatus): { label: string; className: string } {
  if (raw === 'completed') return { label: 'Completed', className: 'bg-green-100 text-green-800 border-green-200' };
  if (raw === 'hard_stop') return { label: 'Hard Stop', className: 'bg-red-100 text-red-800 border-red-200' };
  if (raw === 'abandoned') return { label: 'Abandoned', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  return { label: 'In progress', className: 'bg-blue-100 text-blue-800 border-blue-200' };
}

function Chip({ label, className }: { label: string; className: string }) {
  return (
    <span className={['inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium', className].join(' ')}>
      {label}
    </span>
  );
}

export default function TechDiagnosticsPage() {
  const { accessToken, technician } = useTechAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<TechDiagnosticSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);
    try {
      const rows = await listTechDiagnosticSessions(accessToken);
      setSessions(rows);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const startNew = async () => {
    if (!accessToken) return;

    setCreating(true);
    setError(null);
    try {
      let payload: Parameters<typeof createTechDiagnosticSession>[1] = {};

      try {
        const assignment = await getTechCurrentAssignment(accessToken);
        const current = assignment.current_assignment;

        if (current) {
          payload = {
            job_number: current.job.number,
            successware_job_id: current.job.id,
            location_id: current.location?.id || null,
            job_details: (current.job_details as Record<string, unknown>) || {},
          };
        }
      } catch {
        // Not fatal; allow starting a diagnostic without an assignment.
      }

      const session = await createTechDiagnosticSession(accessToken, payload);
      navigate(`/diagnostics/${session.id}`);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const sessionRows = useMemo(() => sessions || [], [sessions]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Diagnostics</h1>
          <p className="text-gray-600 mt-1">
            {technician?.full_name
              ? `HVAC diagnostic sessions for ${technician.full_name}.`
              : 'HVAC diagnostic sessions.'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={startNew}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            disabled={creating}
          >
            <PlusCircle className="w-4 h-4" />
            Start Diagnostic
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent sessions</h2>
          <div className="text-xs text-gray-500">Showing last {Math.min(sessionRows.length, 25)} sessions</div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Started</th>
                <th className="py-2 pr-4">Job</th>
                <th className="py-2 pr-4">Location</th>
                <th className="py-2 pr-4">Step</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2">Safety</th>
              </tr>
            </thead>
            <tbody>
              {sessionRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-gray-500">
                    No diagnostic sessions yet.
                  </td>
                </tr>
              ) : (
                sessionRows.map((row) => {
                  const status = formatSessionStatus(row.status);

                  return (
                    <tr
                      key={row.id}
                      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/diagnostics/${row.id}`)}
                    >
                      <td className="py-3 pr-4 whitespace-nowrap">{formatDateTime(row.started_at)}</td>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{row.job_number || '—'}</div>
                        <div className="text-xs text-gray-500">{row.successware_job_id || ''}</div>
                      </td>
                      <td className="py-3 pr-4">{row.location_id || '—'}</td>
                      <td className="py-3 pr-4">{row.current_step || '—'}</td>
                      <td className="py-3 pr-4">
                        <Chip label={status.label} className={status.className} />
                      </td>
                      <td className="py-3">
                        {row.safety_gate_passed ? (
                          <Chip label="Passed" className="bg-green-100 text-green-800 border-green-200" />
                        ) : (
                          <Chip label="Not yet" className="bg-yellow-100 text-yellow-800 border-yellow-200" />
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
